/**
 * SpinWheel Component
 *
 * Main fortune wheel with 48 segments for jackpot spins.
 * Features smooth CSS-based animation and visual feedback.
 */

import { memo, useState, useRef, useCallback, useEffect } from 'react';
import { WheelSegment } from './WheelSegment';
import { MAIN_WHEEL_SEGMENTS } from '../../types/jackpot';
import type { SpinResult } from '../../types/jackpot';

interface SpinWheelProps {
  onSpinComplete: (result: SpinResult) => void;
  onSpinStart?: () => void;
  spinResult?: SpinResult | null;
  disabled?: boolean;
  isSpinning?: boolean;
  className?: string;
}

/**
 * Helper to sleep for animations with cancellation support
 */
function sleep(ms: number): { promise: Promise<void>; cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout>;
  const promise = new Promise<void>((resolve) => {
    timeoutId = setTimeout(resolve, ms);
  });
  return {
    promise,
    cancel: () => clearTimeout(timeoutId),
  };
}

/**
 * Calculate animation degrees from slot index
 * Backend returns slot_index, we need to convert to degrees for wheel rotation
 */
function calculateAnimationDegrees(slotIndex: number, totalSlots: number): number {
  const degreesPerSlot = 360 / totalSlots;
  // Calculate base rotation to land on the slot
  // Wheel rotates clockwise, pointer is at top, so we need to rotate to bring slot to top
  const baseRotation = slotIndex * degreesPerSlot;
  // Add randomness within slot for visual variety (stay within 80% of slot)
  const randomOffset = Math.random() * (degreesPerSlot * 0.8);
  return baseRotation + randomOffset;
}

export const SpinWheel = memo(function SpinWheel({
  onSpinComplete,
  onSpinStart,
  spinResult,
  disabled = false,
  isSpinning: externalIsSpinning = false,
  className = '',
}: SpinWheelProps) {
  const [rotation, setRotation] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const wheelRef = useRef<SVGSVGElement>(null);
  const isMountedRef = useRef(true);
  const sleepCancelRef = useRef<(() => void) | null>(null);

  // Track mounted state and cleanup pending sleeps
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (sleepCancelRef.current) {
        sleepCancelRef.current();
      }
    };
  }, []);

  // Handle spin result from parent
  useEffect(() => {
    if (spinResult && !isAnimating) {
      handleSpinAnimation(spinResult);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinResult]);

  const handleSpinAnimation = useCallback(
    async (result: SpinResult) => {
      setIsAnimating(true);

      // Calculate target rotation from slot_index (backend doesn't return animation_degrees)
      const fullSpins = 5;
      const targetDegrees = calculateAnimationDegrees(
        result.main_wheel.slot_index,
        MAIN_WHEEL_SEGMENTS.length
      );
      const totalRotation = rotation + fullSpins * 360 + targetDegrees;

      setRotation(totalRotation);

      // Wait for animation to complete (5 seconds spin time)
      const { promise, cancel } = sleep(5000);
      sleepCancelRef.current = cancel;
      await promise;
      sleepCancelRef.current = null;

      if (!isMountedRef.current) return;

      setIsAnimating(false);
      onSpinComplete(result);
    },
    [rotation, onSpinComplete]
  );

  const handleSpinClick = useCallback(() => {
    if (disabled || isAnimating || externalIsSpinning) return;
    onSpinStart?.();
  }, [disabled, isAnimating, externalIsSpinning, onSpinStart]);

  const isDisabled = disabled || isAnimating || externalIsSpinning;

  return (
    <div className={`relative ${className}`}>
      {/* Pointer at top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 z-10">
        <div
          className="w-0 h-0
                     border-l-[12px] border-r-[12px] border-t-[20px]
                     border-l-transparent border-r-transparent border-t-amber-400
                     drop-shadow-lg"
        />
      </div>

      {/* Wheel container */}
      <div
        className="w-80 h-80 rounded-full shadow-2xl"
        style={{
          transform: `rotate(${rotation}deg)`,
          transition: isAnimating ? 'transform 5s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
        }}
      >
        <svg
          ref={wheelRef}
          viewBox="0 0 320 320"
          className="w-full h-full"
          style={{ filter: isAnimating ? 'blur(0.5px)' : 'none' }}
        >
          {/* Gradient definitions */}
          <defs>
            <linearGradient id="jackpot-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FFD700" />
              <stop offset="50%" stopColor="#FFA500" />
              <stop offset="100%" stopColor="#FFD700" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Outer ring */}
          <circle cx="160" cy="160" r="158" fill="none" stroke="#1e293b" strokeWidth="4" />

          {/* Segments */}
          {MAIN_WHEEL_SEGMENTS.map((segment, index) => (
            <WheelSegment
              key={segment.id}
              index={index}
              totalSegments={MAIN_WHEEL_SEGMENTS.length}
              prize={segment.prize}
              color={segment.color}
              isJackpot={segment.isJackpot}
              radius={150}
            />
          ))}

          {/* Center decoration */}
          <circle cx="160" cy="160" r="45" fill="#1e293b" />
          <circle cx="160" cy="160" r="42" fill="url(#jackpot-gradient)" />
        </svg>
      </div>

      {/* Center spin button */}
      <button
        onClick={handleSpinClick}
        disabled={isDisabled}
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                   w-20 h-20 rounded-full z-20
                   bg-gradient-to-br from-amber-400 to-amber-600
                   text-white font-bold text-lg
                   shadow-lg transition-all duration-200
                   ${
                     isDisabled
                       ? 'opacity-50 cursor-not-allowed'
                       : 'hover:shadow-amber-400/50 hover:scale-105 active:scale-95'
                   }
                   ${isAnimating ? 'animate-pulse' : ''}`}
      >
        {isAnimating || externalIsSpinning ? '...' : 'SPIN'}
      </button>

      {/* Glow effect when spinning */}
      {isAnimating && (
        <div className="absolute inset-0 rounded-full pointer-events-none animate-pulse">
          <div
            className="w-full h-full rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(251,191,36,0.3) 0%, rgba(251,191,36,0) 70%)',
            }}
          />
        </div>
      )}
    </div>
  );
});
